import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { LazyLoadEvent, MessageService } from 'primeng/api';
import { CompanyGateway } from 'src/app/application/gateway/CompanyGateway';
import { ImportOrderGateway } from 'src/app/application/gateway/ImportOrderGateway';
import { LoadInvoiceGateway } from 'src/app/application/gateway/LoadInvoiceGateway';
import { ProfileGateway } from 'src/app/application/gateway/ProfileGateway';
import { Company } from 'src/app/domain/models/Company';
import { availableDeliveryForInvoice, CreateInvoiceRequest, SearchAvailableDeliveriesForInvoiceRequest } from 'src/app/domain/models/LoadInvoice';
import { LoadProfiles } from 'src/app/domain/models/LoadOrder';
import { Exporter, Importer, Profile, ProfileShortInfo } from 'src/app/domain/models/Profile';
import { ProfileUtils } from 'src/app/ui/util/profile-utils';


@Component({
  selector: 'app-admin-load-invoice',
  templateUrl: './admin-load-invoice.component.html',
  styleUrls: ['./admin-load-invoice.component.css']
})
export class AdminLoadInvoiceComponent implements OnInit {

  // Estado
  loading: boolean = false;
  filterForm: FormGroup = new FormGroup({});
  deliveries: availableDeliveryForInvoice[] = [];
  selectedDeliveries: availableDeliveryForInvoice[] = [];
  showResults: boolean = false;

  // Opciones de filtros
  profiles: ProfileShortInfo[] = [];
  loadProfiles: LoadProfiles = {
    importers: [],
    exporters: [],
    forwarders: [],
    forwarderCompanies: [] };

  totalCount = 0;
  pageSize = 100;

  // Datos filtrados
  filteredImporters: Importer[] = [];
  filteredExporters: Exporter[] = [];
  filteredForwarders: string[] = [];

  allSelected: boolean = false;

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private translate: TranslateService,
    private _profileService: ProfileGateway,
    private _companyService: CompanyGateway,
    private _importOrderService: ImportOrderGateway,
    private _loadInvoiceService: LoadInvoiceGateway
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.getProfilesByType(1);
  }

  companies: Company[] = [];
  byProfile: boolean = true;

  initForm(): void {
    this.filterForm = this.fb.group({
      profileId: [''],
      companyId: [''],
      startDate: ['', Validators.required],
      endDate: ['', Validators.required],
      importerId: [''],
      exporterId: [''],
      forwarderId: ['']
    });
  }

  request: SearchAvailableDeliveriesForInvoiceRequest = {
    referenceId: 0,
    startDate: '',
    endDate: '',
    byProfile: true
  };

  profileTypeOptions: any[] = ProfileUtils.getProfileTypeOptions();
  selectedProfileType: number = 1;

  get canSearch(): boolean {
    const reference = this.byProfile ? this.filterForm.get('profileId')?.value : this.filterForm.get('companyId')?.value;
    const startDate = this.filterForm.get('startDate')?.value;
    const endDate = this.filterForm.get('endDate')?.value;
    return !!(reference && startDate && endDate);
  }

  onProfileChange(profileId: number): void {
    // Reset optional filters cuando cambia el profile
    this.resetOptionalFilters();

    // Limpiar resultados
    this.resetValues();
    this.getFilterCompanies(profileId);
  }

  onDateChange(): void {
    // Limpiar resultados cuando cambian las fechas
    this.resetValues();
  }

  searchDeliveries(): void {
    if (!this.canSearch) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Advertencia',
        detail: 'Complete los filtros obligatorios'
      });
      this.showResults = false;
      return;
    }else if(!this.showResults && this.canSearch) {
      this.buildInvoiceRequest();
      this.showResults = true;
    }else{
      this.buildInvoiceRequest();
      this.getAvailableDeliveries();
    }
  }

  getAvailableDeliveriesEvent(event: LazyLoadEvent): void {
    if (this.canSearch) {
      this.getAvailableDeliveries();
      if(event.first && event.rows )
      {
        const pageNumber = Math.floor(event.first / event.rows) + 1;
        const pageSize = event.rows;
        this.getAvailableDeliveries(pageNumber, pageSize);
      }else{
        this.getAvailableDeliveries();
      }
    }
  }

  buildInvoiceRequest() {
    if(this.byProfile) {
      this.request.referenceId = this.filterForm.get('profileId')?.value;
    }else{
      this.request.referenceId = this.filterForm.get('companyId')?.value;
    }
  }

  getAvailableDeliveries(pageNumber: number = 1, pageSize: number = 30): void {
    this.buildInvoiceRequest();
    this.deliveries = [];
    this.selectedDeliveries = [];
    this._loadInvoiceService.getAvailableDeliveriesForInvoice(
      { ...this.request,
      pageNumber,
      pageSize,
      ...this.filterForm.value,
      startDate: moment(this.filterForm.get('startDate')?.value).format('YYYY-MM-DD'),
      endDate: moment(this.filterForm.get('endDate')?.value).format('YYYY-MM-DD'),
      byProfile: this.byProfile
    },

    ).subscribe({
      next: (v) => {
        this.deliveries = v.items;
        this.totalCount = v.totalCount;
        this.loading = false;
      },
      error: (e) => {
        console.log(e);
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant(
            'MessageService.Summary.FailedAction',
          ),
          detail: this.translate.instant(
            'MessageService.LoadInvoice.ManyRetrieveError',
          ),
        });
      }
   });
  }

  applyOptionalFilters(): void {
    let filtered = [...this.deliveries];

    const importerId = this.filterForm.get('importerId')?.value;
    if (importerId) {
      const importer = this.loadProfiles.importers.find(i => i.id === importerId);
      if (importer) {
        filtered = filtered.filter(d => d.importerName === importer.companyName);
      }
    }

    const exporterId = this.filterForm.get('exporterId')?.value;
    if (exporterId) {
      const exporter = this.loadProfiles.exporters.find(e => e.id === exporterId);
      if (exporter) {
        filtered = filtered.filter(d => d.exporterName === exporter.companyName);
      }
    }

    const forwarderName = this.filterForm.get('forwarderName')?.value;
    if (forwarderName) {
      filtered = filtered.filter(d => d.forwarderCompanyName === forwarderName);
    }

    this.deliveries = filtered;
  }

  onSelectionChange(event: any): void {
    this.allSelected = event.checked;
  }

  getSelectedDeliveryIds(): number[] {
    return this.selectedDeliveries.map(d => d.deliveryId);
  }

  clearFilters(): void {
    this.filterForm.reset();
    this.deliveries = [];
    this.selectedDeliveries = [];
    this.showResults = false;
  }

  continueWithInvoicing(): void {
    if (this.selectedDeliveries.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Advertencia',
        detail: 'Seleccione al menos una carga'
      });
      return;
    }

    // Aquí se continuaría con el flujo de facturación
    const deliveryIds = this.getSelectedDeliveryIds();
    var request: CreateInvoiceRequest = {
      deliveryIds,
      referenceId: this.request.referenceId,
      searchAvailableLoadRequest: {
        ...this.request,
        startDate: moment(this.filterForm.get('startDate')?.value).format('YYYY-MM-DD'),
        endDate: moment(this.filterForm.get('endDate')?.value).format('YYYY-MM-DD'),
        ...this.filterForm.value
      },
      allSelected: this.allSelected,
      byProfile: this.byProfile
    }


    this._loadInvoiceService.createInvoice(request).subscribe({
      next: (v) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: `${this.selectedDeliveries.length} carga(s) seleccionada(s) para facturación`
        });

        this.getAvailableDeliveries(); // Refrescar la lista de cargas disponibles después de crear la factura

      },
      error: (e) => {
        console.log(e);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Ocurrió un error al crear la factura'
        });
      }
    });

  }

  onChangeProfileType(type: number)
  {
    this.resetOptionalFilters();
    this.resetValues();
    if(type == 5) {
      this.getForwarderCompany();
    } else if(type == 1 || type == 2) {
      this.getProfilesByType(type);
    }else{
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant(
          'MessageService.Summary.FailedAction',
        ),
        detail: this.translate.instant(
          'MessageService.ProgramsReport.InvalidProfileType',),
      });
    }
  }

  getProfilesByType(type: number) {
    if(type == 1) {
      this.getExporters();
    } else if(type == 2) {
      this.getImporters();
    }
    this.byProfile = true;
  }

  getForwarderCompany()
  {
    this.loading = true;
    this._companyService.getAllByCompanyType(0).subscribe({
      next: (v) => {
        this.companies = v;
        this.byProfile = false;
        this.loading = false;
      },
      error: (e) => {
        console.log(e);
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant(
            'MessageService.Summary.FailedAction',
          ),
          detail: this.translate.instant(
            'MessageService.Company.ManyRetrieveError',
          ),
        });
      }
    });
  }

  getExporters() {
    this.profiles = [];
    this.loading = true;
    this._profileService.getAllExporters().subscribe({
      next: (v) => {
        this.profiles = v;
        this.loading = false;
      },
      error: (e) => {
        console.log(e);
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant(
            'MessageService.Summary.FailedAction',
          ),
          detail: this.translate.instant(
            'MessageService.Exporter.ManyRetrieveError',
          ),
        });
      },
      complete: () => console.info('Exporters query successfully'),
    });
  }

  getImporters() {
    this.profiles = [];
    this.loading = true;
    this._profileService.getAllImporters().subscribe({
      next: (v) => {
        this.profiles = v;
        this.loading = false;
      },
      error: (e) => {
        console.log(e);
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant(
            'MessageService.Summary.FailedAction',
          ),
          detail: this.translate.instant(
            'MessageService.Importer.ManyRetrieveError',
          ),
        });
      },
      complete: () => console.info('Importers query successfully'),
    });
  }

  getFilterCompanies(profileId: number){
    this._importOrderService.getLoadProfiles(profileId).subscribe({
      next: (v) => {
        this.loadProfiles = v;
        this.loading = false;
      },
      error: (e) => {
        console.log(e);
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant(
            'MessageService.Summary.FailedAction',
          ),
          detail: this.translate.instant(
            'MessageService.Company.ManyRetrieveError',
          ),
        });
      },
      complete: () => console.info('Load profiles query successfully'),
    });
  }

  resetValues() {
    this.deliveries = [];
    this.selectedDeliveries = [];
    this.totalCount = 0;
  }

  resetOptionalFilters() {
    this.filterForm.patchValue({
      importerId: '',
      exporterId: '',
      forwarderId: ''
    });
  }

}
